import { StorageError } from './supabase/errors'

/**
 * `bucketId` 采用「黑名单」式校验：只拦截**一定会破坏 URL path 语义**或
 * **明显不安全**的字符，其余字符一律放行，给服务端命名规则保留灵活性。
 *
 * `bucketId` 会被**未编码**地直接拼进 `/bucket/{bucketId}`、
 * `/object/{bucketId}/{name}` 等路径，因此以下字符必须禁止：
 * - 控制字符与空白：`\x00-\x20`（含空格、Tab、换行）与 DEL(`\x7F`)；
 * - URL 路径分隔/结构字符：`/`、`\`、`?`、`#`；
 * - 百分号 `%`：避免与 percent-encoding 产生二义（双重解码风险）。
 *
 * 其余字符（字母、数字、`.`、`_`、`-`、以及大多数可见符号、Unicode 等）
 * 是否合法交由服务端判定，客户端不做过度限制。
 */
const FORBIDDEN_BUCKET_ID_PATTERN = /[\x00-\x20\x7f/\\?#%]/

/** `bucketId` 最大长度（仅作防御性上界，避免异常超长输入）。 */
const BUCKET_ID_MAX_LENGTH = 256

/** 判断 `bucketId` 是否为合法格式（不抛错版本）。 */
export function isValidBucketId(bucketId: unknown): bucketId is string {
  return (
    typeof bucketId === 'string'
    && bucketId.length > 0
    && bucketId.length <= BUCKET_ID_MAX_LENGTH
    && !FORBIDDEN_BUCKET_ID_PATTERN.test(bucketId)
  )
}

/**
 * 校验 `bucketId` 格式，非法时抛出 `StorageError`。
 *
 * 在 `handleOperation` 包裹内调用时，异常会被转成 `{ data: null, error }` 返回；
 * 在 `from()` 等同步入口调用时则直接抛出。
 *
 * @param bucketId 待校验的 Bucket ID。
 * @param caller 调用方方法名，用于错误信息定位（如 `from`、`createBucket`）。
 */
export function assertValidBucketId(bucketId: unknown, caller: string): asserts bucketId is string {
  if (typeof bucketId !== 'string' || bucketId.length === 0) {
    throw new StorageError(`[storage.${caller}] bucketId is required`)
  }
  if (bucketId.length > BUCKET_ID_MAX_LENGTH) {
    throw new StorageError(`[storage.${caller}] invalid bucketId: length must be 1-${BUCKET_ID_MAX_LENGTH}`)
  }
  const forbidden = FORBIDDEN_BUCKET_ID_PATTERN.exec(bucketId)
  if (forbidden) {
    const message = `[storage.${caller}] invalid bucketId "${bucketId}": `
      + `contains forbidden character ${JSON.stringify(forbidden[0])}; `
      + 'must not contain whitespace, control chars, "/", "\\", "?", "#" or "%"'
    throw new StorageError(message)
  }
}
